import React, { ReactElement, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, useParams, Navigate } from "react-router-dom";
import Login from "./pages/Login/Login";
import ForgotPassword from "./pages/Login/ForgotPassword";
import ResetPassword from "./pages/Login/ResetPassword";
import AccountSetup from "./pages/Login/AccountSetup";
import LotDashboard from "./pages/LotDashboard";
import Revenue from "./pages/Dashboard/Revenue";
import Occupancy from "./pages/Dashboard/Occupancy";
import Segmentation from "./pages/Dashboard/Segmentation";
import Emissions from "./pages/Dashboard/Emissions";
import DwellTime from "./pages/Dashboard/DwellTime";
import ParkedCars from "./pages/Occupants";
import Settings from "./pages/Settings";
import AdvancedSettings from "./pages/AdvancedSettings";
import PlateRegistry from "./pages/PlateRegistry";
import Notifications from "./pages/Notifications";
import Violators from "./pages/Violators";
import Addons from "./pages/Addons";
import ParallelAI from "./pages/ParallelAI";
import Mapper from "./pages/Mapper";
import Users from "./pages/Users";
import Cameras from "./pages/Superadmin/Cameras";
import ALPR from "./pages/Superadmin/ALPR";
import Sessions from "./pages/Superadmin/Sessions";
import Disputes from "./pages/Superadmin/Disputes";
import Docs from "./pages/Superadmin/Docs";
import Agents from "./pages/Superadmin/Agents";
import Sidebar from "./components/Sidebar";
import ProtectedRoute from "./utils/ProtectedRoute";
import LotRoleRoute from "./utils/LotRoleRoute";
import SuperadminRoute from "./utils/SuperadminRoute";
import { UserProvider } from "./contexts/UserContext";
import { LotProvider } from "./contexts/LotContext";
import { MultiLotProvider } from "./contexts/MultiLotContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import UserRedirect from "./utils/UserRedirect";
import "./App.css";
import LotLayout from "./components/LotLayout";
import Dashboard from "./pages/Dashboard/Dashboard";
import { DirtyStateProvider } from './contexts/DirtyStateContext';
import SuperadminHeader from "./components/SuperadminHeader";
import DemoLotBanner from "./components/DemoLotBanner/DemoLotBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { WalkthroughProvider } from "./components/Walkthrough/WalkthroughContext";
import WalkthroughOverlay from "./components/Walkthrough/WalkthroughOverlay";

const ExternalRedirect = ({ url }: { url: string }) => {
  useEffect(() => {
    window.location.href = url;
  }, [url]);

  return null;
};

// LotProviderWrapper - extracts lotId from URL and provides it to LotProvider
const LotProviderWrapper = ({ children }: { children: ReactElement }) => {
  const { lotId } = useParams<{ lotId: string }>();
  return (
    <LotProvider lotId={lotId}>
      <MultiLotProvider>
        {children}
      </MultiLotProvider>
    </LotProvider>
  );
};

// PageLayout with LotProvider for lot-specific routes
const LotPageLayout = ({ children }: { children: ReactElement }) => (
  <LotProviderWrapper>
    <div className="app-container">
      <Sidebar />
      <div className="content">{children}</div>
    </div>
  </LotProviderWrapper>
);

// Regular PageLayout for non-lot-specific routes
const PageLayout = ({ children }: { children: ReactElement }) => (
  <div className="app-container">
    <Sidebar />
    <div className="content">{children}</div>
  </div>
);

// The main App component now just sets up routes and providers
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <DirtyStateProvider>
          <Router>
            <UserProvider>
              <WalkthroughProvider>
              <SuperadminHeader />
              <DemoLotBanner />
              <WalkthroughOverlay />
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Navigate to="/login" />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/setup-account" element={<AccountSetup />} />
              <Route
                path="/terms-of-service"
                element={
                  <ExternalRedirect url="https://www.parkwithparallel.com/legal/operator/terms-of-service?redirect-from-operator=true" />
                }
              />
              <Route
                path="/privacy-policy"
                element={
                  <ExternalRedirect url="https://www.parkwithparallel.com/legal/operator/privacy-policy?redirect-from-operator=true" />
                }
              />
              <Route path="/mapper" element={<Mapper />} />

              {/* Protected routes */}
              <Route
                element={<ProtectedRoute><Outlet /></ProtectedRoute>}
              >
                <Route path="/dashboard" element={<UserRedirect />} />
                <Route path="/lot-dashboard" element={<LotDashboard />} />

                {/* Persistent Lot layout for all lot-specific pages */}
                <Route path="/lot/:lotId" element={<LotLayout />}>
                  <Route path="dashboard" element={<Dashboard />}>
                    <Route index element={<Revenue />} />
                    <Route path="revenue" element={<Revenue />} />
                    <Route path="occupancy" element={<Occupancy />} />
                    <Route path="segmentation" element={<Segmentation />} />
                    <Route path="emissions" element={<Emissions />} />
                    <Route path="dwell-time" element={<DwellTime />} />
                  </Route>
                  <Route path="parallel-ai" element={<ParallelAI />} />
                  <Route path="occupants" element={<ParkedCars />} />
                  <Route path="registry" element={<PlateRegistry />} />
                  <Route path="violators" element={<Violators />} />
                  <Route path="notifications" element={<Notifications />} />
                  {/* Protected pages are siblings, wrapped with LotRoleRoute for role verification */}
                  <Route
                    path="settings"
                    element={
                      <LotRoleRoute allowedRoles={['owner', 'operator']}>
                        <Settings />
                      </LotRoleRoute>
                    }
                  />
                  <Route
                    path="advanced"
                    element={
                      <LotRoleRoute allowedRoles={['owner', 'operator']}>
                        <AdvancedSettings />
                      </LotRoleRoute>
                    }
                  />
                  <Route
                    path="addons"
                    element={
                      <LotRoleRoute allowedRoles={['owner']}>
                        <Addons />
                      </LotRoleRoute>
                    }
                  />
                  <Route path="users" element={<Users />} />
                  <Route
                    path="superadmin/cameras"
                    element={
                      <SuperadminRoute>
                        <Cameras />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="superadmin/alpr-log"
                    element={
                      <SuperadminRoute>
                        <ALPR />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="superadmin/sessions"
                    element={
                      <SuperadminRoute>
                        <Sessions />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="superadmin/disputes"
                    element={
                      <SuperadminRoute>
                        <Disputes />
                      </SuperadminRoute>
                    }
                  />
                </Route>
                <Route
                  path="/superadmin/docs"
                  element={
                    <SuperadminRoute>
                      <Docs />
                    </SuperadminRoute>
                  }
                />
                <Route
                  path="/superadmin/agents"
                  element={
                    <SuperadminRoute>
                      <Agents />
                    </SuperadminRoute>
                  }
                />
              </Route>
              </Routes>
              </WalkthroughProvider>
            </UserProvider>
          </Router>
        </DirtyStateProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
